import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';
import { FileText, ShieldOff, BookOpen, LayoutGrid, AlertCircle, XCircle, CheckCircle, Mail } from 'lucide-react';
import ContactFormModal from '../ContactForm/ContactFormModal';

const RefundPolicy = () => {
    const dispatch = useDispatch();
    const [activeSection, setActiveSection] = useState('');
    const [openContactForm, setOpenContactForm] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(setIsLoginDialog(false));

        document.documentElement.style.scrollBehavior = 'smooth';

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0px -80% 0px' }
        );

        document.querySelectorAll('section[id]').forEach((section) => {
            observer.observe(section);
        });

        return () => {
            document.documentElement.style.scrollBehavior = '';
            observer.disconnect();
        };
    }, [dispatch]);

    const sections = [
        { id: 'section-1', title: '1. General Policy',               icon: ShieldOff },
        { id: 'section-2', title: '2. Smart Journal Subscriptions',   icon: BookOpen },
        { id: 'section-3', title: '3. Smart Assessment Packs',        icon: LayoutGrid },
        { id: 'section-4', title: '4. Exceptions',                    icon: AlertCircle },
        { id: 'section-5', title: '5. Cancellation Policy',           icon: XCircle },
        { id: 'section-6', title: '6. Processing Approved Refunds',   icon: CheckCircle },
        { id: 'section-7', title: '7. Contact Us',                    icon: Mail },
    ];

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    const sectionData = [
        {
            id: 'section-1',
            icon: ShieldOff,
            title: '1. General Policy',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    All purchases made on VISIST.AI are <strong>non-refundable</strong> once the service has been activated or used. We encourage users to review plan details and features before completing a purchase.
                </p>
            ),
        },
        {
            id: 'section-2',
            icon: BookOpen,
            title: '2. Smart Journal Subscriptions',
            content: (
                <ul className="space-y-3">
                    {[
                        'Subscriptions (First Step, Foundation, Performance, Elite) are non-refundable once activated.',
                        'Unused video uploads or remaining validity period cannot be refunded.',
                        'Subscriptions will renew automatically where applicable unless cancelled before the renewal date.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-3',
            icon: LayoutGrid,
            title: '3. Smart Assessment Packs',
            content: (
                <ul className="space-y-3">
                    {[
                        'Assessment packs purchased by academies or coaches are non-refundable once activated.',
                        'Unused assessments within a pack cannot be refunded or carried forward beyond the validity period.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-4',
            icon: AlertCircle,
            title: '4. Exceptions',
            content: (
                <>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        Refunds may be considered <strong>only</strong> under the following exceptional conditions:
                    </p>
                    <ul className="space-y-3 mb-5">
                        {[
                            'Duplicate payment made due to a technical error on our platform.',
                            'Payment was successfully processed but the service was not activated due to a verified system failure.',
                        ].map((item, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                                <span className="text-gray-700">{item}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="rounded-xl p-4 border-2" style={{ background: 'linear-gradient(to right, #FEF3C7, #FFFBEB)', borderColor: '#F59E0B' }}>
                        <p className="text-sm font-semibold" style={{ color: '#92400E' }}>
                            ⚠️ All such requests must be raised within <strong>7 days</strong> of the payment date. Requests made after this window will not be eligible for review.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'section-5',
            icon: XCircle,
            title: '5. Cancellation Policy',
            content: (
                <ul className="space-y-3">
                    {[
                        'Users may cancel future subscription renewals at any time through their account settings.',
                        'No refunds will be provided for the current active subscription period upon cancellation.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-6',
            icon: CheckCircle,
            title: '6. Processing of Approved Refunds',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    If a refund request is reviewed and approved, it will be processed within <strong>7–10 business days</strong> to the original payment method used at the time of purchase.
                </p>
            ),
        },
        {
            id: 'section-7',
            icon: Mail,
            title: '7. Contact Us',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    For refund-related queries or to raise an exception request, please contact our support team at:{' '}
                    <a
                        href="mailto:sales@visist.ai"
                        className="font-semibold no-underline hover:underline"
                        style={{ color: '#247E45' }}
                    >
                        sales@visist.ai
                    </a>
                </p>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-deep-charcoal text-white">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-deep-charcoal to-gray-900"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 py-20 md:py-32 relative z-10">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
                            <ShieldOff className="w-4 h-4 text-emerald" />
                            <span className="text-sm font-semibold text-white">Transparent Policies</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight uppercase text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">
                            Refund Policy
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-3xl">
                            This Refund Policy applies to all purchases made on the VISIST.AI platform, including Smart Journal subscriptions and Smart Assessment packs.
                        </p>
                        <p className="mt-6 text-gray-400 text-sm">
                            Last updated: <span className="font-semibold text-white">April 2026</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-12 md:py-20">
                <div className="flex flex-col lg:flex-row gap-12 max-w-7xl mx-auto">

                    {/* Sticky Table of Contents */}
                    <aside className="lg:w-80 flex-shrink-0">
                        <div className="lg:sticky lg:top-24 space-y-6">
                            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
                                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5" style={{ color: '#247E45' }} />
                                    Table of Contents
                                </h2>
                                <nav className="space-y-1">
                                    {sections.map((section) => {
                                        const Icon = section.icon;
                                        return (
                                            <button
                                                key={section.id}
                                                onClick={() => scrollToSection(section.id)}
                                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 group ${
                                                    activeSection === section.id
                                                        ? 'font-semibold'
                                                        : 'text-slate-600 hover:bg-gray-50 hover:text-gray-900'
                                                }`}
                                                style={activeSection === section.id ? { backgroundColor: '#D9F0DE', color: '#026B33' } : {}}
                                            >
                                                <Icon
                                                    className="w-4 h-4 flex-shrink-0"
                                                    style={{ color: activeSection === section.id ? '#026B33' : '#9CA3AF' }}
                                                />
                                                <span className="text-sm leading-tight">{section.title}</span>
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>

                            {/* Quick Contact Card */}
                            <div className="rounded-2xl p-6 border-2" style={{ background: 'linear-gradient(to bottom right, #D9F0DE, #E8FFE8)', borderColor: '#247E45' }}>
                                <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#026B33' }}>
                                    <Mail className="w-5 h-5" />
                                    Refund Request?
                                </h3>
                                <p className="text-sm mb-3" style={{ color: '#026B33' }}>
                                    Contact us about a refund or billing issue
                                </p>
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="inline-flex items-center gap-2 text-sm font-semibold hover:underline transition-colors"
                                    style={{ color: '#026B33' }}
                                >
                                    Click Here
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Content */}
                    <main className="flex-1 min-w-0">
                        {/* Introduction */}
                        <div className="prose prose-slate max-w-none mb-12">
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10">
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    At{' '}
                                    <a href="https://www.visist.ai" target="_blank" rel="noopener noreferrer" className="font-semibold no-underline hover:underline" style={{ color: '#247E45' }}>
                                        VISIST.AI
                                    </a>
                                    , we strive to provide transparent and fair billing practices. Please read this policy carefully before making any purchase on our platform.
                                </p>
                            </div>
                        </div>

                        {/* Section Cards */}
                        {sectionData.map((section) => {
                            const Icon = section.icon;
                            return (
                                <section key={section.id} id={section.id} className="mb-12 scroll-mt-24">
                                    <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10 hover:border-emerald transition-all duration-300">
                                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D9F0DE' }}>
                                                <Icon className="w-5 h-5" style={{ color: '#247E45' }} />
                                            </div>
                                            {section.title}
                                        </h2>
                                        {section.content}
                                    </div>
                                </section>
                            );
                        })}

                        {/* Footer CTA */}
                        <div className="rounded-2xl p-8 md:p-10 text-white text-center relative overflow-hidden bg-deep-charcoal">
                            <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald/10 rounded-full blur-3xl"></div>
                            <div className="relative z-10">
                                <h3 className="text-2xl font-bold mb-3 text-white">Need Help with a Refund?</h3>
                                <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                                    Our team is ready to assist. Reach out to us and we'll look into your request promptly.
                                </p>
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-white font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                                    style={{ color: '#026B33' }}
                                >
                                    <Mail className="w-5 h-5" />
                                    Contact Us
                                </button>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ContactFormModal
                isOpen={openContactForm}
                onClose={() => setOpenContactForm(false)}
            />
        </div>
    );
};

export default RefundPolicy;
